<template>
  <div class="user-impersonation">
    <el-card class="impersonation-card">
      <template #header>
        <div class="card-header">
          <h3>ユーザー代理ログイン</h3>
          <el-button
            v-if="impersonationStatus.isImpersonating"
            type="danger"
            size="small"
            @click="stopImpersonation"
            :loading="isLoading"
          >
            代理ログイン終了
          </el-button>
        </div>
      </template>

      <!-- Current Status -->
      <div v-if="impersonationStatus.isImpersonating" class="current-status">
        <el-alert
          title="現在代理ログイン中"
          type="warning"
          :closable="false"
          show-icon
        >
          <p>
            <strong>対象ユーザー:</strong> 
            {{ impersonationStatus.session.targetUser.name }}
          </p>
          <p>
            <strong>残り時間:</strong> 
            {{ formatRemainingTime(impersonationStatus.session.expiresAt) }}
          </p>
        </el-alert>
      </div>

      <!-- User Selection -->
      <div v-if="!impersonationStatus.isImpersonating" class="user-selection">
        <el-form @submit.prevent="startImpersonation">
          <el-form-item label="代理ログイン対象ユーザー">
            <UserSearchSelect
              v-model="selectedUserId"
              :exclude-current-user="true"
              @user-selected="handleUserSelected"
              style="width: 100%"
            />
          </el-form-item>

          <el-form-item label="継続時間">
            <el-select v-model="durationHours" style="width: 200px">
              <el-option label="1時間" :value="1" />
              <el-option label="2時間" :value="2" />
              <el-option label="4時間" :value="4" />
              <el-option label="8時間" :value="8" />
              <el-option label="24時間" :value="24" />
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button
              type="primary"
              @click="startImpersonation"
              :loading="isLoading"
              :disabled="!selectedUserId"
            >
              代理ログイン開始
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, onMounted, inject } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { differenceInMinutes, differenceInHours, isPast } from 'date-fns'
import axios from 'axios'
import UserSearchSelect from './UserSearchSelect.vue'

export default {
  name: 'UserImpersonation',
  components: {
    UserSearchSelect
  },
  setup() {
    const auth = inject('Auth')
    const backendUrl = inject('BackendUrl')
    
    const selectedUserId = ref('')
    const durationHours = ref(1)
    const isLoading = ref(false)
    
    const impersonationStatus = reactive({
      isImpersonating: false,
      session: null
    })


    // Check current impersonation status
    const checkImpersonationStatus = async () => {
      try {
        const idToken = (await auth.getIdTokenClaims({}))?.__raw
        const response = await axios.get(`${backendUrl}/v1/admin/impersonation-status`, {
          headers: { Authorization: `Bearer ${idToken}` }
        })
        
        impersonationStatus.isImpersonating = response.data.isImpersonating
        impersonationStatus.session = response.data.session
      } catch (error) {
        console.error('Failed to check impersonation status:', error)
      }
    }

    // Start impersonation
    const startImpersonation = async () => {
      if (!selectedUserId.value) {
        ElMessage.warning('ユーザーを選択してください')
        return
      }

      try {
        await ElMessageBox.confirm(
          '選択されたユーザーとして代理ログインを開始しますか？',
          '確認',
          {
            confirmButtonText: 'はい',
            cancelButtonText: 'キャンセル',
            type: 'warning',
          }
        )

        isLoading.value = true
        const idToken = (await auth.getIdTokenClaims({}))?.__raw
        const response = await axios.post(
          `${backendUrl}/v1/admin/impersonate`,
          {
            targetUserId: selectedUserId.value,
            durationHours: durationHours.value
          },
          {
            headers: { Authorization: `Bearer ${idToken}` }
          }
        )

        // Store the impersonation token
        localStorage.setItem('impersonationToken', response.data.sessionToken)
        
        ElMessage.success('代理ログインを開始しました')
        await checkImpersonationStatus()
        
        // Refresh the page to apply impersonation context
        window.location.reload()
        
      } catch (error) {
        if (error.response) {
          ElMessage.error(error.response.data.error || '代理ログイン開始に失敗しました')
        } else if (error !== 'cancel') {
          ElMessage.error('代理ログイン開始に失敗しました')
        }
      } finally {
        isLoading.value = false
      }
    }


    // Stop impersonation
    const stopImpersonation = async () => {
      try {
        await ElMessageBox.confirm(
          '代理ログインを終了しますか？',
          '確認',
          {
            confirmButtonText: 'はい',
            cancelButtonText: 'キャンセル',
            type: 'warning',
          }
        )

        isLoading.value = true
        const idToken = (await auth.getIdTokenClaims({}))?.__raw
        await axios.delete(`${backendUrl}/v1/admin/impersonate`, {
          headers: { Authorization: `Bearer ${idToken}` }
        })

        // Remove the impersonation token
        localStorage.removeItem('impersonationToken')
        
        ElMessage.success('代理ログインを終了しました')
        
        // Refresh the page to remove impersonation context
        window.location.reload()
        
      } catch (error) {
        if (error.response) {
          ElMessage.error(error.response.data.error || '代理ログイン終了に失敗しました')
        } else if (error !== 'cancel') {
          ElMessage.error('代理ログイン終了に失敗しました')
        }
      } finally {
        isLoading.value = false
      }
    }

    // Handle user selection
    const handleUserSelected = (user) => {
      // User selection is already handled by v-model, this is just for additional actions if needed
      console.log('Selected user:', user)
    }

    // Format remaining time using date-fns
    const formatRemainingTime = (dateString) => {
      if (!dateString) return '-'
      const expiry = new Date(dateString)
      const now = new Date()
      
      if (isPast(expiry)) return '期限切れ'
      
      const hours = differenceInHours(expiry, now)
      const minutes = differenceInMinutes(expiry, now) % 60
      
      if (hours > 0) {
        return `${hours}時間${minutes}分`
      } else {
        return `${minutes}分`
      }
    }

    onMounted(async () => {
      await checkImpersonationStatus()
    })

    return {
      selectedUserId,
      durationHours,
      isLoading,
      impersonationStatus,
      startImpersonation,
      stopImpersonation,
      handleUserSelected,
      formatRemainingTime
    }
  }
}
</script>

<style scoped>
.user-impersonation {
  max-width: 1000px;
  margin: 0 auto;
}

.impersonation-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
}

.current-status {
  margin-bottom: 20px;
}

.user-selection {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.users-list h4 {
  margin-bottom: 15px;
  color: #303133;
}
</style>